import type {
  AllStoriesSortOption,
  ContentTypeFilter,
  ExploreStoryCard,
  TimeRangeFilter,
} from "./types";

export type SelectedStoryFilters = Record<string, string[]>;

const TIME_RANGE_MS: Record<Exclude<TimeRangeFilter, "all">, number> = {
  "48h": 48 * 60 * 60 * 1000,
  "7d": 7 * 24 * 60 * 60 * 1000,
  "30d": 30 * 24 * 60 * 60 * 1000,
};

function parsePublishedDate(publishedAt: string): Date | null {
  if (!publishedAt) {
    return null;
  }
  const parsed = new Date(publishedAt);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }
  return parsed;
}

function filterByTimeRange({
  stories,
  timeRange,
}: {
  stories: ExploreStoryCard[];
  timeRange: TimeRangeFilter;
}): ExploreStoryCard[] {
  if (timeRange === "all") {
    return stories;
  }
  const cutoff = new Date(Date.now() - TIME_RANGE_MS[timeRange]);
  return stories.filter((story) => {
    const date = parsePublishedDate(story.publishedAt);
    return date !== null && date >= cutoff;
  });
}

/** Map ContentTypeFilter values to materialType values. */
const CONTENT_TYPE_TO_MATERIAL: Record<string, string> = {
  news: "NEWS",
  workflow: "WORKFLOW",
  release: "RELEASE",
  prompt: "PROMPT",
  deal: "DEAL",
};

function filterByContentType({
  stories,
  contentType,
}: {
  stories: ExploreStoryCard[];
  contentType: ContentTypeFilter;
}): ExploreStoryCard[] {
  if (contentType === "all") {
    return stories;
  }
  const materialType = CONTENT_TYPE_TO_MATERIAL[contentType];
  if (!materialType) {
    return stories;
  }
  return stories.filter((story) => story.materialType === materialType);
}

export function applyStoryFilters({
  stories,
  selectedFilters,
  searchQuery,
  sort,
  contentType = "all",
  timeRange = "all",
}: {
  stories: ExploreStoryCard[];
  selectedFilters: SelectedStoryFilters;
  searchQuery?: string;
  sort?: AllStoriesSortOption;
  contentType?: ContentTypeFilter;
  timeRange?: TimeRangeFilter;
}): ExploreStoryCard[] {
  let filtered = [...stories];

  // Search query
  const normalizedQuery = searchQuery?.trim().toLowerCase() ?? "";
  if (normalizedQuery.length > 0) {
    filtered = filtered.filter(
      (story) =>
        story.headline.toLowerCase().includes(normalizedQuery) ||
        story.summary.toLowerCase().includes(normalizedQuery)
    );
  }

  // Content type (tab selection)
  filtered = filterByContentType({ stories: filtered, contentType });

  // Time range
  filtered = filterByTimeRange({ stories: filtered, timeRange });

  // When trending sort is active, cap to 7 days regardless of time range selection
  if (sort === "trending") {
    filtered = filterByTimeRange({ stories: filtered, timeRange: "7d" });
  }

  // Category filter
  const categoryFilters = selectedFilters.category ?? [];
  if (categoryFilters.length > 0) {
    filtered = filtered.filter((story) => {
      if (!story.categorySlug) {
        return false;
      }
      return categoryFilters.includes(story.categorySlug);
    });
  }

  // Product filter (replaces old tool filter — no slug mapping needed)
  const productFilters = selectedFilters.product ?? [];
  if (productFilters.length > 0) {
    const productSet = new Set(productFilters);
    filtered = filtered.filter((story) =>
      story.allTagSlugs.some((tagSlug) => productSet.has(tagSlug))
    );
  }

  // Topic filter (replaces old tag filter)
  const topicFilters = selectedFilters.topic ?? [];
  if (topicFilters.length > 0) {
    const topicSet = new Set(topicFilters);
    filtered = filtered.filter((story) =>
      story.allTagSlugs.some((tagSlug) => topicSet.has(tagSlug))
    );
  }

  // Sorting
  if (sort === "newest" || sort === "oldest") {
    const direction = sort === "newest" ? -1 : 1;
    filtered.sort((a, b) => {
      const dateA = parsePublishedDate(a.publishedAt);
      const dateB = parsePublishedDate(b.publishedAt);
      if (!(dateA || dateB)) {
        return 0;
      }
      if (!dateA) {
        return 1;
      }
      if (!dateB) {
        return -1;
      }
      return direction * (dateA.getTime() - dateB.getTime());
    });
  }

  return filtered;
}
